import {
    differenceInSeconds,
    addDays,
    addMonths,
    setHours,
    setMinutes,
    setSeconds,
    getHours,
    getMinutes,
    getSeconds,
    getDay,
    startOfMonth,
    endOfMonth,
    isSameDay,
    getDaysInMonth,
    isBefore,
} from 'date-fns';

import type { RepeatCycle } from '$lib/types/Components/Countdown/CountdownContent';

const getTimeOfDayInSeconds = (date: Date) => {
    return getHours(date) * 3600 + getMinutes(date) * 60 + getSeconds(date);
};

const getMonthlyTargetDate = (targetDate: Date, now: Date) => {
    const currentDay = targetDate.getDate();
    let adjustedTarget = new Date(now);

    // determine if it's first, middle, or last day
    const daysInCurrentMonth = getDaysInMonth(now);
    const middleDay = Math.ceil(daysInCurrentMonth / 2);

    if (currentDay === 1) {
        // first day
        adjustedTarget = startOfMonth(adjustedTarget);
    } else if (currentDay === getDaysInMonth(targetDate)) {
        // last day
        adjustedTarget = endOfMonth(adjustedTarget);
    } else {
        // middle day or closest to middle
        adjustedTarget.setDate(middleDay);
    }

    // set time components
    adjustedTarget = setHours(adjustedTarget, getHours(targetDate));
    adjustedTarget = setMinutes(adjustedTarget, getMinutes(targetDate));
    adjustedTarget = setSeconds(adjustedTarget, getSeconds(targetDate));

    // if target has passed today, move to next month
    if (isBefore(adjustedTarget, now)) {
        adjustedTarget = addMonths(adjustedTarget, 1);

        // recalculate for the next month --> in case of different month lengths
        if (currentDay !== 1 && !isSameDay(adjustedTarget, endOfMonth(adjustedTarget))) {
            const nextMonthMiddleDay = Math.ceil(getDaysInMonth(adjustedTarget) / 2);

            if (currentDay === Math.ceil(getDaysInMonth(targetDate) / 2)) {
                // if it was middle day, keep it middle in next month
                adjustedTarget.setDate(nextMonthMiddleDay);
            }
        }
    }

    return adjustedTarget;
};

export const getTimeDifferenceByCycle = (cycle: RepeatCycle, datetime: string) => {
    const targetDate = new Date(datetime);
    const now = new Date();

    // Adjust to current timezone
    const timezoneOffset = new Date().getTimezoneOffset();
    targetDate.setMinutes(targetDate.getMinutes() + timezoneOffset);

    switch (cycle) {
        case 'fixed': {
            // using date-fns differenceInSeconds for more accurate calculations
            return differenceInSeconds(targetDate, now);
        }
        case 'daily': {
            // create todays instance of the target time
            let adjustedTarget = new Date(now);
            adjustedTarget = setHours(adjustedTarget, getHours(targetDate));
            adjustedTarget = setMinutes(adjustedTarget, getMinutes(targetDate));
            adjustedTarget = setSeconds(adjustedTarget, getSeconds(targetDate));

            let diff = differenceInSeconds(adjustedTarget, now);

            // if time has passed today, set to tomorrow
            if (diff < 0) {
                adjustedTarget = addDays(adjustedTarget, 1);
                diff = differenceInSeconds(adjustedTarget, now);
            }

            return diff;
        }
        case 'weekly': {
            // check if today is the same weekday as target
            if (getDay(targetDate) === getDay(now)) {
                const targetTimeOfDay = getTimeOfDayInSeconds(targetDate);
                const currentTimeOfDay = getTimeOfDayInSeconds(now);

                // if time has passed today
                if (currentTimeOfDay > targetTimeOfDay) {
                    // set to next week's occurrence
                    let adjustedTarget = new Date(now);
                    adjustedTarget = setHours(adjustedTarget, getHours(targetDate));
                    adjustedTarget = setMinutes(adjustedTarget, getMinutes(targetDate));
                    adjustedTarget = setSeconds(adjustedTarget, getSeconds(targetDate));
                    adjustedTarget = addDays(adjustedTarget, 7);

                    return differenceInSeconds(adjustedTarget, now);
                } else {
                    // set to today's occurrence
                    let adjustedTarget = new Date(now);
                    adjustedTarget = setHours(adjustedTarget, getHours(targetDate));
                    adjustedTarget = setMinutes(adjustedTarget, getMinutes(targetDate));
                    adjustedTarget = setSeconds(adjustedTarget, getSeconds(targetDate));

                    return differenceInSeconds(adjustedTarget, now);
                }
            } else {
                // calculate days until next occurrence
                const daysToAdd = (getDay(targetDate) - getDay(now) + 7) % 7 || 7;

                // set the target to next occurrence
                let adjustedTarget = new Date(now);
                adjustedTarget = setHours(adjustedTarget, getHours(targetDate));
                adjustedTarget = setMinutes(adjustedTarget, getMinutes(targetDate));
                adjustedTarget = setSeconds(adjustedTarget, getSeconds(targetDate));
                adjustedTarget = addDays(adjustedTarget, daysToAdd);

                return differenceInSeconds(adjustedTarget, now);
            }
        }
        case 'monthly': {
            const adjustedTarget = getMonthlyTargetDate(targetDate, now);

            return differenceInSeconds(adjustedTarget, now);
        }
        default:
            return 0;
    }
};
